import { AttendanceMutationRow, upsertAttendanceMember } from './attendance-mutations';
import { masIdsEqual } from './mas-ids';

export type ScheduleAtCapacityReason = 'at_capacity';

/** Thrown by `enrollInScheduleAtomic` when upsert would exceed schedule capacity. */
export class ScheduleAtCapacityError extends Error {
	readonly code = 'schedule_at_capacity' as const;

	constructor() {
		super('Class is at capacity');
		this.name = 'ScheduleAtCapacityError';
	}
}

export type UpsertAttendanceResult<TAtt extends AttendanceMutationRow> = {
	attendance: TAtt[];
	changed: boolean;
	rejected?: ScheduleAtCapacityReason;
};

/**
 * `enrollInScheduleAtomic` mode `upsert`: add or update a member. New enrollments are
 * rejected when attendance is already at `reserveLimit`; updates to an existing row always apply.
 */
export function upsertAttendanceMemberRespectingCapacity<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
	scheduleId: string,
	reserveLimit: number,
	patch: {
		attended?: boolean;
		memberName?: string;
		notifications?: TAtt['notifications'];
		reserved?: boolean;
		lessonCategory?: string;
	},
): UpsertAttendanceResult<TAtt> {
	const isExisting = attendance.some(a => masIdsEqual(a.id, memberId));
	if (!isExisting && attendance.length >= reserveLimit) {
		return { attendance: [...attendance], changed: false, rejected: 'at_capacity' };
	}

	const upserted = upsertAttendanceMember(attendance, memberId, scheduleId, patch);
	return { attendance: upserted.attendance, changed: upserted.changed };
}
